import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Wand2, Square, RotateCcw } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { RewriteSummary } from "./RewriteSummary";

const Left = ({ items, testid }) => (
  <ul data-testid={testid} className="mt-1 text-xs text-stone-600 list-disc pl-5">
    {items.map((p) => <li key={p.block}>p.{p.n} · {p.label}{p.error ? ` — ${p.error}` : ""}</li>)}
  </ul>
);

export const RewriteAll = ({ kit, weakBlocks, reloadKit }) => {
  const weak = weakBlocks.length;
  const [busy, setBusy] = useState(false);
  const st = kit.style_fix_all;
  const running = st?.status === "running";
  const call = async (path, msg) => {
    setBusy(true);
    try { await http.post(`/kits/${kit.id}/${path}`); toast.message(msg); reloadKit?.(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const done = st ? st.done.length + st.failed.length + (st.kept || []).length : 0;
  const retryN = st ? st.failed.length + (st.kept || []).length : 0;
  const stillLeft = (st?.left || []).filter((p) => weakBlocks.includes(p.block));
  return (
    <div data-testid="rewrite-all" className="mt-3 rounded-md border border-stone-200 bg-white p-3">
      <div className="flex flex-wrap items-center gap-3">
        {!running && weak > 0 && (
          <button data-testid="rewrite-all-btn" disabled={busy} onClick={() => call("style-fix-all", `Rewriting ${weak} weak page(s) one after another — you can leave this page`)}
            className="inline-flex items-center gap-1.5 rounded-sm bg-[#2B2A26] px-3 py-1.5 text-xs font-bold text-white hover:bg-black transition-colors disabled:opacity-60">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} Rewrite all {weak} weak page{weak === 1 ? "" : "s"}
          </button>
        )}
        {running && (
          <>
            <Loader2 size={15} className="animate-spin text-[#9A7B2C]" />
            <span data-testid="rewrite-all-progress" className="text-sm text-[#2B2A26]">
              Rewriting page {Math.min(done + 1, st.total)} of {st.total}{st.current ? ` — p.${st.current.n} · ${st.current.label}` : ""}
            </span>
            <button data-testid="rewrite-all-stop" disabled={busy || st.stop} onClick={() => call("style-fix-all/stop", "Stopping after the current page")}
              className="ml-auto inline-flex items-center gap-1 text-xs text-stone-600 hover:text-black disabled:opacity-50">
              <Square size={11} /> {st.stop ? "Stopping…" : "Stop after this page"}
            </button>
          </>
        )}
        {!running && st && (
          <span data-testid="rewrite-all-result" className={`text-xs ${st.status === "done" && !st.failed.length && !(st.kept || []).length ? "text-emerald-700" : "text-amber-800"}`}>
            Last run of {st.total} page{st.total === 1 ? "" : "s"}
            {st.status === "budget" ? " — stopped: the kit's $8 AI budget ran out" : st.status === "stopped" ? " — stopped by you" : ""}. The page check re-runs automatically to refresh the scores.
          </span>
        )}
      </div>
      {running && <div className="mt-2 h-1.5 rounded-full bg-stone-100 overflow-hidden"><div data-testid="rewrite-all-bar" className="h-full bg-[#C9A847] transition-[width] duration-500" style={{ width: `${(done / st.total) * 100}%` }} /></div>}
      {!running && stillLeft.length > 0 && <><p className="mt-2 text-xs font-semibold text-stone-700">Pages still to rewrite:</p><Left items={stillLeft} testid="rewrite-all-left" /></>}
      {!running && st && <RewriteSummary st={st} />}
      {!running && retryN > 0 && (
        <button data-testid="rewrite-retry-btn" disabled={busy} onClick={() => call("style-fix-all/retry", `Retrying ${retryN} kept or failed page(s)`)}
          className="mt-2 inline-flex items-center gap-1.5 rounded-sm border border-[#2B2A26] px-3 py-1.5 text-xs font-bold text-[#2B2A26] hover:bg-[#2B2A26] hover:text-white transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} Retry {retryN} kept or failed page{retryN === 1 ? "" : "s"}
        </button>
      )}
      {st?.status === "budget" && st.error && <p data-testid="rewrite-all-error" className="mt-1 text-xs text-red-700">{st.error}</p>}
    </div>
  );
};
